import React from 'react';
import { Helmet } from 'react-helmet-async';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  author?: string;
  publishedAt?: string;
  modifiedAt?: string;
  price?: string;
  currency?: string;
  availability?: 'in stock' | 'out of stock' | 'preorder';
  brand?: string;
  category?: string;
  sku?: string;
  gtin?: string;
  structuredData?: object[];
  noIndex?: boolean;
  canonical?: string;
}

const DEFAULT_SEO = {
  siteName: 'Begard Gear',
  defaultTitle: 'Begard Gear | Professional Bee Sting Prevention Clothing & Equipment',
  defaultDescription: 'Shop premium bee sting prevention clothing and safety equipment. Professional-grade protection for beekeepers, outdoor workers, and nature enthusiasts. Quality gear you can trust.',
  defaultImage: '/logo512.png', // Will use your existing logo
  defaultUrl: process.env.REACT_APP_SITE_URL || 'https://begardgear.com',
  twitterHandle: '@BegardGear', // Update with your actual handle
  locale: 'en_US',
  themeColor: '#fcba03',
};

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  author,
  publishedAt,
  modifiedAt,
  price,
  currency = 'USD',
  availability = 'in stock',
  brand = 'Begard Gear',
  category,
  sku,
  gtin,
  structuredData = [],
  noIndex = false,
  canonical,
}) => {
  const siteTitle = title 
    ? `${title} | ${DEFAULT_SEO.siteName}`
    : DEFAULT_SEO.defaultTitle;
  
  const siteDescription = description || DEFAULT_SEO.defaultDescription;
  const siteImage = image || DEFAULT_SEO.defaultImage;
  const siteUrl = url || DEFAULT_SEO.defaultUrl;
  const canonicalUrl = canonical || siteUrl;

  // Generate WebSite structured data for search functionality
  const websiteData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: DEFAULT_SEO.siteName,
    url: DEFAULT_SEO.defaultUrl,
    description: DEFAULT_SEO.defaultDescription,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${DEFAULT_SEO.defaultUrl}/products?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };

  // Generate organization structured data
  const organizationData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: DEFAULT_SEO.siteName,
    url: DEFAULT_SEO.defaultUrl,
    logo: `${DEFAULT_SEO.defaultUrl}/logo512.png`,
    description: DEFAULT_SEO.defaultDescription,
    sameAs: [
      // Add your social media URLs here when available
      // 'https://facebook.com/begardgear',
      // 'https://instagram.com/begardgear',
      // 'https://twitter.com/begardgear'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@begardgear.com',
      availableLanguage: ['English']
    },
    founder: {
      '@type': 'Person',
      name: 'Begard Gear Founders',
      description: 'Siblings from Minnesota Northwoods dedicated to outdoor safety'
    }
  };

  // Generate product structured data if this is a product page
  const productData = type === 'product' && price ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: title,
    description: siteDescription,
    image: siteImage,
    brand: {
      '@type': 'Brand',
      name: brand
    },
    category: category,
    sku: sku,
    gtin: gtin,
    offers: {
      '@type': 'Offer',
      price: price,
      priceCurrency: currency,
      availability: `https://schema.org/${availability.replace(' ', '')}`,
      url: siteUrl,
      seller: {
        '@type': 'Organization',
        name: DEFAULT_SEO.siteName
      }
    }
  } : null;

  // Combine all structured data
  const allStructuredData = [
    websiteData,
    organizationData,
    ...(productData ? [productData] : []),
    ...structuredData
  ];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{siteTitle}</title>
      <meta name="description" content={siteDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex,nofollow" />}
      
      {/* Open Graph */}
      <meta property="og:title" content={title || DEFAULT_SEO.defaultTitle} />
      <meta property="og:description" content={siteDescription} />
      <meta property="og:image" content={siteImage} />
      <meta property="og:url" content={siteUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={DEFAULT_SEO.siteName} />
      <meta property="og:locale" content={DEFAULT_SEO.locale} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={DEFAULT_SEO.twitterHandle} />
      <meta name="twitter:creator" content={DEFAULT_SEO.twitterHandle} />
      <meta name="twitter:title" content={title || DEFAULT_SEO.defaultTitle} />
      <meta name="twitter:description" content={siteDescription} />
      <meta name="twitter:image" content={siteImage} />
      
      {/* Article specific meta tags */}
      {type === 'article' && publishedAt && (
        <meta property="article:published_time" content={publishedAt} />
      )}
      {type === 'article' && modifiedAt && (
        <meta property="article:modified_time" content={modifiedAt} />
      )}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}
      
      {/* Product specific meta tags */}
      {type === 'product' && price && (
        <>
          <meta property="product:price:amount" content={price} />
          <meta property="product:price:currency" content={currency} />
        </>
      )}
      
      {/* Theme color */}
      <meta name="theme-color" content={DEFAULT_SEO.themeColor} />
      
      {/* Structured Data */}
      {allStructuredData.map((data, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(data)
          }}
        />
      ))}
    </Helmet>
  );
};

export default SEO;
